import { ActionButtonCell, CheckboxCell, CheckboxHeader, TextCell, TextWithHoverAction } from '@/components/tables/contentFields';
import EmptyTableView from '@/components/tables/EmptyTableView';
import { NonSortableColumnHeader } from '@/components/tables/NonSortableColumnHeader';
import type { SortState } from '@/components/tables/SortDirection';
import Table, { TableBody, TableHeader } from '@/components/tables/TableBasics';
import TableLoadingView from '@/components/tables/TableLoadingView';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Company } from '../types';
import { useCompanyLevelName } from '../hooks';
import { formatDate } from '@/utils/dateUtils';

interface CompaniesTableProps {
  companies: Company[];
  isLoading: boolean;
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  sortState: SortState;
  onSortChange: (column: string) => void;
  onRowClick: (company: Company) => void;
}


export function CompaniesTable({
  companies,
  isLoading,
  selectedIds,
  onSelectionChange,
  onRowClick,
}: CompaniesTableProps) {
  const { t } = useTranslation();
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);

  // Translated company level names, shared via the company levels cache.
  const { resolveLevelName } = useCompanyLevelName();

  const allSelected =
    companies.length > 0 && selectedIds.size === companies.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const handleSelectAll = () => {
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(companies.map((c) => c.id)));
    }
  };

  const handleSelectRow = (id: string) => {
    const newSelection = new Set(selectedIds);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    onSelectionChange(newSelection);
  };

  if (isLoading && companies.length === 0) {
    return (
      <TableLoadingView isLoading={isLoading} rows={5} columns={8} withHeader={true} />
    );
  }

  if (companies.length === 0) {
    return (
      <EmptyTableView />
    );
  }

  return (
    <Table className="min-h-0 h-full" tableClassName="min-w-full divide-border">
      <TableHeader>
        <CheckboxHeader
          checked={allSelected}
          onChange={handleSelectAll}
          ariaLabel={t('companies.table.selectAll')}
          indeterminate={someSelected}
        />

        {/*column="name"
            sortState={sortState}
            onSort={onSortChange}
            */}
        <NonSortableColumnHeader
          label={t('companies.table.columns.name')}
          minWidth="270px"
        />

        {/*column="category"
            sortState={sortState}
            onSort={onSortChange}
            */}
        <NonSortableColumnHeader
          label={t('companies.table.columns.category')}
          minWidth="36px"
        />

        {/*column="totalTools"
            sortState={sortState}
            onSort={onSortChange}
            */}
        <NonSortableColumnHeader
          label={t('companies.table.columns.totalTools')}
          minWidth="42.5px"
        />

        {/*column="notAssignedTools"
            sortState={sortState}
            onSort={onSortChange}
            */}
        <NonSortableColumnHeader
          label={t('companies.table.columns.notAssignedTools')}
          minWidth="42.5px"
        />

        <NonSortableColumnHeader
          label={t('companies.table.columns.mainContact')}
        />

        <NonSortableColumnHeader
          label={t('companies.table.columns.contactPerson')}
          minWidth="75px"
        />

        {/*column="lastActive"
            sortState={sortState}
            onSort={onSortChange}
            */}
        <NonSortableColumnHeader
          label={t('companies.table.columns.lastActive')}
        />
      </TableHeader>

      <TableBody>
        {companies.map((company) => {
          const levelName = company.level
            ? resolveLevelName(company.level.id) ?? company.level.name
            : '';

            
          return <tr
            key={company.id}
            className="hover:bg-surface-darker/50"
            onMouseEnter={() => setHoveredRowId(company.id)}
            onMouseLeave={() => setHoveredRowId(null)}
          >
            <CheckboxCell
              checked={selectedIds.has(company.id)}
              onChange={() => handleSelectRow(company.id)}
              ariaLabel={t('companies.table.selectRow', { name: company.name })}
            />

            <TextWithHoverAction
              value={company.name}
              isHovered={hoveredRowId === company.id}
              onCellClick={() => onRowClick(company)}
              onActionClick={() => onRowClick(company)}
              actionLabel={t('companies.table.open')}
            />

            <TextCell value={levelName} />

            <TextCell value={company.totalTools} />

            <TextCell value={company.notAssignedTools} />

            <ActionButtonCell
              text={company.mainContact.name}
              onClick={(e) => {
                e.stopPropagation();
              }}
            />

            <td className="size-px whitespace-nowrap px-4 py-1">
              <a
                href={`mailto:${company.contactPerson.email}`}
                className="text-sm text-primary hover:text-primary-darker hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {company.contactPerson.name}
              </a>
            </td>

            <TextCell value={formatDate(company.lastActive)} />
          </tr>
        })}
      </TableBody>
    </Table>
  );
}


